import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Loader2, Megaphone, ShieldAlert, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Sidebar, MobileNav } from "./components/Sidebar";
import { DashboardOverview } from "./components/DashboardOverview";
import { PostComposer } from "./components/PostComposer";
import { ContentCalendar } from "./components/ContentCalendar";
import { ApprovalFeed } from "./components/ApprovalFeed";
import { AutopilotSettings } from "./components/AutopilotSettings";
import { AnalyticsView } from "./components/AnalyticsView";
import { BrandsView } from "./components/BrandsView";
import { AccountsView } from "./components/AccountsView";
import { DraftsView } from "./components/DraftsView";
import { NewsView } from "./components/NewsView";
import { BlogView } from "./components/BlogView";
import { TeamView } from "./components/TeamView";
import { Onboarding } from "./components/Onboarding";
import AdminConsole from "./components/admin/AdminConsole";
import AuthScreen from "./components/auth/AuthScreen";
import { InvitePage, SignupPage } from "./components/auth/AccountPages";
import Landing from "./marketing/Landing";
import Maintenance from "./marketing/Maintenance";
import Legal from "./marketing/Legal";
import { Changelog, Dpa, Security, Status } from "./marketing/Trust";
import api from "./lib/api";
import { SessionProvider, usePlatformStatus, useSession } from "./lib/session";

const CONNECT_MSG = {
  linkedin: ["success", "LinkedIn account connected 🎉"],
  meta: ["success", "Facebook & Instagram connected 🎉"],
  linkedin_error: ["error", "LinkedIn connection failed. Please try again."],
  meta_error: ["error", "Meta connection failed. Please try again."],
  linkedin_notconfigured: ["error", "LinkedIn isn't set up on this platform yet."],
  meta_notconfigured: ["error", "Meta isn't set up on this platform yet."],
  limit: ["error", "Your plan's account limit is reached. Upgrade to connect more."],
  threads: ["success", "Threads account connected 🎉"],
  none_configured: ["error", "No social apps are set up yet - add the developer keys first."],
  threads_error: ["error", "Threads connection failed."],
  threads_notconfigured: ["error", "Threads isn't set up on this platform yet."],
  linkedin_pages: ["success", "LinkedIn Pages connected 🎉"],
  linkedin_pages_error: ["error", "LinkedIn Pages connection failed. The LinkedIn app needs the Community Management API, and you must be an admin of the page."],
  linkedin_pages_none: ["error", "LinkedIn found no pages where you're an admin. Ask a page admin to add you, then connect again."],
  linkedin_pages_notconfigured: ["error", "LinkedIn isn't set up on this platform yet."],
};

export const VIEWS = ["dashboard", "composer", "news", "blog", "drafts", "calendar", "approvals",
  "analytics", "brands", "accounts", "autopilot", "team"];

const Spinner = () => (
  <div className="min-h-screen bg-ac-bg flex items-center justify-center">
    <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
  </div>
);

const Unreachable = ({ onRetry }) => (
  <div className="min-h-screen bg-ac-bg flex items-center justify-center px-6">
    <div className="max-w-md text-center">
      <div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-5">
        <AlertTriangle className="w-6 h-6 text-rose-300" />
      </div>
      <h1 className="font-heading text-2xl font-bold text-slate-50 mb-2">Can&apos;t reach the server</h1>
      <p className="text-sm text-slate-400 mb-6">
        You are still signed in - the backend just isn&apos;t answering. It is usually the database being
        unreachable rather than the app itself.
      </p>
      <button onClick={onRetry}
        className="px-5 py-2.5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold transition-colors">
        Try again
      </button>
    </div>
  </div>
);

/* A customer who is signed in while the platform is in maintenance. */
const PausedForMaintenance = ({ onSignOut }) => (
  <div className="min-h-screen bg-ac-bg flex items-center justify-center px-6">
    <div className="max-w-md text-center">
      <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-5">
        <Wrench className="w-6 h-6 text-amber-300" />
      </div>
      <h1 className="font-heading text-2xl font-bold text-slate-50 mb-2">We&apos;ll be right back</h1>
      <p className="text-sm text-slate-400 mb-6">
        AutoCruise is down for maintenance. Your workspace and scheduled posts are safe - check back shortly.
      </p>
      <button onClick={onSignOut} className="text-sm text-slate-400 hover:text-slate-200 underline">Sign out</button>
    </div>
  </div>
);

/* Set the tab title and whether crawlers may index this page: the public
   site is indexable once it launches, everything behind sign-in never is. */
function usePageMeta(title, indexable) {
  useEffect(() => {
    document.title = title;
    let tag = document.querySelector('meta[name="robots"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.name = "robots";
      document.head.appendChild(tag);
    }
    tag.content = indexable ? "index, follow" : "noindex, nofollow";
  }, [title, indexable]);
}

function RequireAuth({ admin = false, children }) {
  const { status, me, isAdmin, refresh, signOut } = useSession();
  const location = useLocation();
  if (status === "checking") return <Spinner />;
  if (status === "unreachable") return <Unreachable onRetry={refresh} />;
  if (status !== "authed") return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  if (admin && !isAdmin) return <Navigate to="/app" replace />;
  if (me?.platform?.maintenance && !isAdmin) return <PausedForMaintenance onSignOut={signOut} />;
  return children;
}

function Banners() {
  const { me, isAdmin } = useSession();
  const navigate = useNavigate();
  const p = me?.platform || {};
  const tone = { info: "bg-indigo-500/10 border-indigo-500/25 text-indigo-200",
                 warning: "bg-amber-500/10 border-amber-500/25 text-amber-200",
                 success: "bg-emerald-500/10 border-emerald-500/25 text-emerald-200" }[p.announcement_level || "info"];
  const homeWs = (me?.workspaces || []).some((w) => w.id === me?.workspace?.id);
  return (
    <div className="space-y-2 mb-6">
      {isAdmin && p.maintenance && (
        <button onClick={() => navigate("/admin/platform")}
          className="w-full flex items-center gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-2.5 text-left text-sm text-amber-200">
          <Wrench className="w-4 h-4 shrink-0" />
          Maintenance mode is on - visitors see the maintenance page and only you can sign in.
          <span className="ml-auto font-semibold underline">Manage</span>
        </button>
      )}
      {isAdmin && !homeWs && (
        <div className="flex items-center gap-2.5 rounded-xl border border-fuchsia-500/25 bg-fuchsia-500/10 px-4 py-2.5 text-sm text-fuchsia-200">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          You&apos;re inside <strong className="font-semibold">{me?.workspace?.name}</strong> as super admin. Changes here affect this customer.
          <button onClick={() => navigate("/admin/workspaces")} className="ml-auto font-semibold underline">Back to admin</button>
        </div>
      )}
      {p.announcement && (
        <div className={`flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-sm ${tone}`}>
          <Megaphone className="w-4 h-4 shrink-0" /> {p.announcement}
        </div>
      )}
    </div>
  );
}

function Workspace() {
  const { view: raw } = useParams();
  const view = VIEWS.includes(raw) ? raw : "dashboard";
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, refresh } = useSession();
  const [pending, setPending] = useState(0);
  const [resumeId, setResumeId] = useState(null);
  usePageMeta(`${view === "dashboard" ? "Dashboard" : view[0].toUpperCase() + view.slice(1)} · AutoCruise`, false);

  const setView = (v) => navigate(v === "dashboard" ? "/app" : `/app/${v}`);
  const goTo = (v) => { setResumeId(null); setView(v); };
  const resumeDraft = (id) => { setResumeId(id); setView("composer"); };

  useEffect(() => {
    api.get("/stats").then(({ data }) => setPending(data.pending || 0)).catch(() => {});
  }, [view]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get("connected");
    if (c === "all") {
      const names = { linkedin: "LinkedIn", linkedin_pages: "LinkedIn Pages", meta: "Facebook & Instagram", threads: "Threads" };
      const list = (k) => (params.get(k) || "").split(",").filter(Boolean).map((x) => names[x] || x).join(", ");
      if (list("ok")) toast.success(`Connected: ${list("ok")}`, { duration: 10000 });
      if (list("failed")) toast.error(`Not connected: ${list("failed")} - try those again from Accounts.`,
        { duration: 20000, description: params.get("reason") || undefined });
      navigate("/app/accounts", { replace: true });
    } else if (c) {
      const m = CONNECT_MSG[c];
      const reason = params.get("reason");
      // The network's own reason, so a failed connection says what to fix.
      if (m) toast[m[0] === "error" ? "error" : "success"](m[1], reason ? { description: reason, duration: 25000 } : undefined);
      else if (reason) toast.error(reason, { duration: 25000 });
      navigate("/app/accounts", { replace: true });
    }
  }, [location.search, navigate]);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/login");
  };
  const refreshPending = () => api.get("/stats").then(({ data }) => setPending(data.pending || 0)).catch(() => {});

  return (
    <div className="App min-h-screen bg-ac-bg">
      <Toaster position="top-right" theme="dark" richColors />
      <div className="flex">
        <Sidebar active={view} onChange={setView} pendingCount={pending} onSignOut={handleSignOut} />
        <div className="flex-1 min-w-0">
          <MobileNav active={view} onChange={setView} onSignOut={handleSignOut} />
          <main className="relative z-10 px-4 sm:px-8 py-6 sm:py-10 max-w-[1400px] mx-auto">
            <Banners />
            <AnimatePresence mode="wait">
              <motion.div key={view}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}>
                {view === "dashboard" && <><Onboarding goTo={goTo} /><DashboardOverview goTo={goTo} /></>}
                {view === "composer" && <PostComposer onDone={goTo} resumeId={resumeId} />}
                {view === "news" && <NewsView />}
                {view === "blog" && <BlogView seedUrl="" />}
                {view === "drafts" && <DraftsView onResume={resumeDraft} />}
                {view === "calendar" && <ContentCalendar />}
                {view === "approvals" && <ApprovalFeed onChange={refreshPending} />}
                {view === "analytics" && <AnalyticsView />}
                {view === "brands" && <BrandsView />}
                {view === "accounts" && <AccountsView goTo={goTo} />}
                {view === "autopilot" && <AutopilotSettings onDone={goTo} onChanged={refresh} />}
                {view === "team" && <TeamView />}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
}

function LoginPage() {
  const { status, refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  usePageMeta("Sign in · AutoCruise", false);
  const destination = location.state?.from || "/app";
  if (status === "authed") return <Navigate to={destination} replace />;
  return (
    <>
      <Toaster position="top-right" theme="dark" richColors />
      <AuthScreen onAuthed={async () => {
        const me = await refresh();
        navigate(me?.is_super_admin && destination === "/app" ? "/app" : destination, { replace: true });
      }} />
    </>
  );
}

function PublicHome() {
  const platform = usePlatformStatus();
  const { status, isAdmin } = useSession();
  const maintenance = platform?.maintenance;
  usePageMeta("AutoCruise · Your content team, on autopilot", platform ? !maintenance : false);
  if (!platform || status === "checking") return <div className="min-h-screen bg-ac-bg" />;
  if (maintenance && !isAdmin) return <Maintenance platform={platform} />;
  return <Landing platform={platform} signedIn={status === "authed"} previewing={maintenance && isAdmin} />;
}

function PublicPage({ children, title }) {
  usePageMeta(title, true);
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route path="/" element={<PublicHome />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<PublicPage title="Create your workspace · AutoCruise"><SignupPage /></PublicPage>} />
          <Route path="/invite/:token" element={<PublicPage title="Join a workspace · AutoCruise"><InvitePage /></PublicPage>} />
          <Route path="/terms" element={<PublicPage title="Terms of Service · AutoCruise"><Legal kind="terms" /></PublicPage>} />
          <Route path="/privacy" element={<PublicPage title="Privacy Policy · AutoCruise"><Legal kind="privacy" /></PublicPage>} />
          <Route path="/security" element={<PublicPage title="Security · AutoCruise"><Security /></PublicPage>} />
          <Route path="/dpa" element={<PublicPage title="Data processing agreement · AutoCruise"><Dpa /></PublicPage>} />
          <Route path="/status" element={<PublicPage title="Status · AutoCruise"><Status /></PublicPage>} />
          <Route path="/changelog" element={<PublicPage title="Changelog · AutoCruise"><Changelog /></PublicPage>} />
          <Route path="/app" element={<RequireAuth><Workspace /></RequireAuth>} />
          <Route path="/app/:view" element={<RequireAuth><Workspace /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth admin><AdminConsole /></RequireAuth>} />
          <Route path="/admin/:section" element={<RequireAuth admin><AdminConsole /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}

export default App;
